/**
 * Admin — Revenue Calculation, the missing/conflicting inputs panel (card 1245).
 *
 * The money blockers that are STILL UNANSWERED, from
 * `docs/oval/project-oval-revenue/README.md`, each with
 * the question, exactly what is missing, which page figures it holds up, the named owner,
 * and a link to the source doc.
 *
 * 🔑 NOTHING HERE IS AUTHORED IN THE VIEW. Every string is served by
 * `GET /admin/revenue-calculation/blockers`, which reads the frozen `BLOCKERS` table in
 * `revenueCalculationController.js`. A blocker cannot be quietly answered, softened or
 * dropped by editing a component.
 *
 * 🔑 THE EXPOSURE TOTAL IS THE POINT. It states how many product x term rows currently
 * render `— / Missing` and what gross revenue sits behind them — the size of the hole. Rows
 * that carry no catalog price are counted SEPARATELY rather than folded in as $0, because a
 * zero there would understate the very number this panel exists to publish.
 */

import { formatPrice } from '../../utils/formatters';
import styles from '../../pages/admin/RevenueCalculationPage.module.css';

const DOC_BASE = 'https://github.com/ovalcare/oval/blob/main/';

/** Pure. The size-of-the-hole banner. */
export const ExposureSummary = ({ exposure }) => {
  const rowCount = Number(exposure?.rowCount ?? 0);
  const productCount = Number(exposure?.productCount ?? 0);
  const withGross = Number(exposure?.rowsWithKnownGross ?? 0);
  const noGross = Number(exposure?.rowsWithNoGross ?? 0);
  return (
    <div className={styles.exposure} data-testid="blockers-exposure">
      <div className={styles.exposureFigure}>
        <p className={styles.exposureValue} data-testid="exposure-gross">
          {formatPrice(Number(exposure?.grossRevenueCents ?? 0))}
        </p>
        <p className={styles.exposureLabel}>Gross revenue with no statable cost</p>
      </div>
      <div className={styles.exposureDetail}>
        <p className={styles.exposureCounts} data-testid="exposure-counts">
          <strong>{rowCount}</strong> of {Number(exposure?.totalRowCount ?? 0)} product × term rows
          render <span className={styles.missingToken}>— / Missing</span>, across{' '}
          <strong>{productCount}</strong> products.
        </p>
        <p className={styles.exposureNote}>
          {withGross} of those carry a catalog price and are summed above; {noGross} carry no
          price at all and are counted here rather than added as $0.
        </p>
      </div>
    </div>
  );
};

/** Pure. One blocker card. */
export const BlockerCard = ({ blocker, isSelected, onSelect }) => (
  <li
    className={isSelected ? styles.blockerCardActive : styles.blockerCard}
    data-testid={`blocker-${blocker.id}`}
    data-blocker={blocker.id}
  >
    <button
      type="button"
      className={styles.blockerHead}
      onClick={() => onSelect(isSelected ? null : blocker.id)}
      aria-pressed={isSelected}
      data-testid={`blocker-select-${blocker.id}`}
    >
      <span className={styles.blockerCode}>{blocker.id}</span>
      <span className={styles.blockerQuestion}>{blocker.question}</span>
      <span className={styles.blockerCount}>
        {blocker.affectedRowCount} row{blocker.affectedRowCount === 1 ? '' : 's'}
      </span>
    </button>

    <dl className={styles.blockerBody}>
      <dt className={styles.blockerTerm}>What is missing</dt>
      <dd className={styles.blockerDef}>{blocker.whatIsMissing}</dd>

      <dt className={styles.blockerTerm}>Blocks</dt>
      <dd className={styles.blockerDef}>
        <ul className={styles.blocksList}>
          {(blocker.blocks ?? []).map((figure) => (
            <li key={figure} className={styles.blocksItem}>{figure}</li>
          ))}
        </ul>
      </dd>

      <dt className={styles.blockerTerm}>Owner</dt>
      <dd className={styles.blockerDef}>
        {blocker.owner}
        {blocker.counterparty ? ` · answer held with ${blocker.counterparty}` : ''}
        {blocker.severity === 'confirmation' ? ' · confirmation only' : ''}
      </dd>

      <dt className={styles.blockerTerm}>Source</dt>
      <dd className={styles.blockerDef}>
        <a
          className={styles.blockerLink}
          href={`${DOC_BASE}${blocker.sourceDoc}`}
          target="_blank"
          rel="noreferrer"
        >
          {blocker.sourceDoc}
        </a>
      </dd>
    </dl>
  </li>
);

/**
 * Pure. The panel. `selectedBlocker` is read from the URL by the page, so a blocker
 * selection survives a reload and can be shared as a link.
 */
const RevenueBlockersPanel = ({ blockers, exposure, sourceDoc, answered, selectedBlocker, onSelectBlocker }) => (
  <div className={styles.blockers} data-testid="blockers-panel">
    <ExposureSummary exposure={exposure} />

    <p className={styles.blockersLead} data-testid="blockers-lead">
      {(blockers ?? []).length} still-unanswered money blockers, verbatim from{' '}
      <a className={styles.blockerLink} href={`${DOC_BASE}${sourceDoc}`} target="_blank" rel="noreferrer">
        {sourceDoc}
      </a>
      . None of them is answered, defaulted or guessed anywhere in this codebase. Select one to
      filter the product economics table to the rows it blocks.
      {(answered ?? []).length > 0 && (
        <span data-testid="blockers-answered-note">
          {' '}<strong>{(answered ?? []).join(' and ')}</strong> used to be listed here and{' '}
          {(answered ?? []).length === 1 ? 'was' : 'were'} answered on 2026-08-28 — the
          assumptions panel below carries the ruling that closed{' '}
          {(answered ?? []).length === 1 ? 'it' : 'them'}.
        </span>
      )}
    </p>

    {selectedBlocker && (
      <p className={styles.blockerActiveNote} data-testid="blocker-active-note">
        Filtering the product economics table to rows blocked by <strong>{selectedBlocker}</strong>.
        <button type="button" className={styles.blockerClear} onClick={() => onSelectBlocker(null)}>
          Clear
        </button>
      </p>
    )}

    <ul className={styles.blockerList}>
      {(blockers ?? []).map((blocker) => (
        <BlockerCard
          key={blocker.id}
          blocker={blocker}
          isSelected={selectedBlocker === blocker.id}
          onSelect={onSelectBlocker}
        />
      ))}
    </ul>
  </div>
);

export default RevenueBlockersPanel;
